import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { MagnifyingGlass, Money, CreditCard, X, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Caja() {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const load = () => {
    api.get("/orders/active").then((r) => setOrders(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  const filtered = orders.filter((o) => {
    if (!search) return true;
    return String(o.table_number).includes(search) || o.mesero_name.toLowerCase().includes(search.toLowerCase());
  });

  const pay = async (method) => {
    if (!active) return;
    try {
      await api.post(`/orders/${active.id}/pay`, { payment_method: method });
      toast.success(`Cobrado · ${method === "efectivo" ? "Efectivo" : "Tarjeta"}`);
      setActive(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const cancel = async () => {
    if (!active || !cancelReason.trim()) return;
    try {
      await api.post(`/orders/${active.id}/cancel`, { reason: cancelReason });
      toast.success("Orden cancelada");
      setActive(null);
      setShowCancel(false);
      setCancelReason("");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr,480px] min-h-[calc(100vh-4rem)]">
      <section className="p-6 md:p-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-2">Caja</div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Órdenes pendientes de cobro</h1>
          </div>
          <div className="px-4 py-2 rounded-xl bg-orange-100 text-orange-700 font-bold text-sm">{filtered.length} mesas activas</div>
        </div>

        <div className="relative mb-5">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" weight="bold" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="caja-search"
            placeholder="Buscar por mesa o mesero..."
            className="w-full h-14 pl-12 pr-4 rounded-2xl bg-white border-2 border-zinc-200 focus:border-brand outline-none text-base font-medium"
          />
        </div>

        <div className="space-y-3">
          {filtered.length === 0 && (
            <div className="bg-white rounded-3xl border-2 border-dashed border-zinc-200 py-16 text-center text-zinc-500">
              No hay órdenes pendientes
            </div>
          )}
          {filtered.map((o) => {
            const allReady = o.items.every((i) => i.status === "ready");
            return (
              <button
                key={o.id}
                onClick={() => setActive(o)}
                data-testid={`caja-order-${o.id}`}
                className={`w-full text-left bg-white rounded-2xl border-2 p-5 transition-all hover:-translate-y-0.5 hover:shadow-md ${active?.id === o.id ? "border-brand ring-4 ring-red-100" : "border-zinc-200"}`}
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-2xl ${allReady ? "bg-emerald-500" : "bg-zinc-200"} text-white flex items-center justify-center font-display font-black text-2xl`}>
                      {o.table_number}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display text-lg font-bold">Mesa {o.table_number}</span>
                        <span className="font-display text-sm font-bold text-brand bg-red-50 px-2 py-0.5 rounded-md">#{String(o.order_number || 0).padStart(3, "0")}</span>
                      </div>
                      <div className="text-sm text-zinc-500">{o.mesero_name} · {o.items.length} producto(s)</div>
                      <div className="text-xs text-zinc-400 mt-0.5">
                        {allReady ? <span className="text-emerald-600 font-semibold">✓ Lista para servir</span> : <span>En preparación</span>}
                      </div>
                    </div>
                  </div>
                  <div className="font-display font-black text-3xl text-brand">${o.total.toFixed(2)}</div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Detail panel */}
      <aside className="bg-white border-l border-zinc-200 flex flex-col max-h-[calc(100vh-4rem)]">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <Money size={48} className="text-zinc-300 mb-3" weight="duotone" />
            <div className="font-display text-xl font-bold">Sin orden seleccionada</div>
            <p className="text-sm text-zinc-500 mt-1">Toca una orden para verla en detalle y cobrar.</p>
          </div>
        ) : (
          <>
            <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Detalle</div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-2xl font-black">Mesa {active.table_number}</span>
                  <span className="font-display text-sm font-bold text-brand bg-red-50 px-2 py-0.5 rounded-md">#{String(active.order_number || 0).padStart(3, "0")}</span>
                </div>
              </div>
              <button onClick={() => setActive(null)} className="text-zinc-400 hover:text-red-500" data-testid="caja-close-detail"><X size={22} weight="bold" /></button>
            </div>

            <div className="flex-1 overflow-y-auto pos-scroll p-4 space-y-2">
              {active.items.map((it) => (
                <div key={it.item_id} className="bg-zinc-50 rounded-xl p-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-bold text-sm">×{it.qty} {it.name}</div>
                      {it.variants.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {it.variants.map((v) => <span key={v} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-200 font-medium">{v}</span>)}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-sm">${(it.price * it.qty).toFixed(2)}</div>
                      <div className={`text-[10px] uppercase font-bold tracking-wider ${it.status === "ready" ? "text-emerald-600" : "text-orange-500"}`}>{it.status === "ready" ? "Listo" : "En cocina"}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 border-t border-zinc-200 space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="text-sm uppercase tracking-wider font-semibold text-zinc-500">Total</span>
                <span className="font-display font-black text-4xl text-brand">${active.total.toFixed(2)}</span>
              </div>

              {showCancel ? (
                <div className="space-y-2">
                  <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Motivo de cancelación..."
                    className="w-full h-12 px-4 rounded-xl border-2 border-zinc-200 focus:border-red-500 outline-none" data-testid="caja-cancel-reason" />
                  <div className="flex gap-2">
                    <button onClick={() => setShowCancel(false)} className="flex-1 h-12 rounded-xl bg-zinc-100 font-semibold">Volver</button>
                    <button onClick={cancel} disabled={!cancelReason.trim()} data-testid="caja-cancel-confirm"
                      className="flex-1 h-12 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50">Confirmar Cancelación</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => pay("efectivo")} data-testid="caja-pay-efectivo" className="h-16 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold inline-flex items-center justify-center gap-2 active:scale-95 transition-all">
                      <Money size={22} weight="fill" /> Efectivo
                    </button>
                    <button onClick={() => pay("tarjeta")} data-testid="caja-pay-tarjeta" className="h-16 rounded-2xl bg-zinc-900 hover:bg-zinc-800 text-white font-bold inline-flex items-center justify-center gap-2 active:scale-95 transition-all">
                      <CreditCard size={22} weight="fill" /> Tarjeta
                    </button>
                  </div>
                  <button onClick={() => setShowCancel(true)} data-testid="caja-cancel-btn" className="w-full h-11 rounded-xl border-2 border-red-200 text-red-600 hover:bg-red-50 font-semibold text-sm">
                    Cancelar orden
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
